import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Checkbox, Empty } from 'antd';
import { SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

import {
  PendingCommunicationsHistory,
  CommunicationsHistory,
  HistoricPendingCommunication,
  CommunicationsPendingHistoryForm,
} from 'types';
import { historicPendingCommunicationActions } from 'common/config/store';
import { useIntl } from 'common/config/i18n';
import StyledTable from 'components/organisms/styled-table';
import CommunicationHistoryModal from 'components/organisms/communication-history-modal';
import PaginationBlock from 'components/molecules/pagination';
import { UnstyledButton } from 'common/styling/mixins';

interface Props {
  data?: HistoricPendingCommunication;
  onClickDelete: () => void;
  handleSelectMessages: (e: CheckboxChangeEvent, record: PendingCommunicationsHistory) => void;
  canWriteDefferedCommunications: boolean;
  filterValues?: CommunicationsPendingHistoryForm;
}

const acceptStyle = (segments: number, index: number) =>
  segments === index
    ? {
        paddingTop: '5px',
        paddingBottom: '5px',
      }
    : { borderBottom: '1px solid #f5f5f5', paddingTop: '5px', paddingBottom: '5px' };

const HistorisationPendingList: React.FC<Props> = ({
  data,
  onClickDelete,
  handleSelectMessages,
  canWriteDefferedCommunications,
  filterValues,
}) => {
  const { t } = useIntl();
  const dispatch = useDispatch();
  const [modalVisible, setModalVisible] = useState(false);
  const [record, setRecord] = useState<CommunicationsHistory | null>(null);
  const columns = [
    {
      title: <div>{t('table', 'CHART_CREATION_DATE')}</div>,
      render: (record: PendingCommunicationsHistory) => (
        <div>{moment(record.creationDate).format('DD/MM/YYYY HH:mm')}</div>
      ),
      key: 'Date',
    },
    {
      title: <div>{t('table', 'CHART_TRANSMITTER')}</div>,
      dataIndex: 'sender',
      key: 'Transmitter',
    },
    {
      title: <div>{t('table', 'CHART_PROCESS')}</div>,
      dataIndex: 'processName',
      key: 'Process',
    },
    {
      title: <div>{t('table', 'CHART_SITUATION')}</div>,
      dataIndex: 'situationName',
      key: 'Situation',
    },
    {
      title: <div>{t('table', 'CHART_TRAIN')}</div>,
      dataIndex: 'serviceName',
      key: 'Train',
    },
    {
      title: <div>{t('table', 'CHART_DATE')}</div>,
      render: (record: PendingCommunicationsHistory) => <div>{moment(record.serviceDate).format('DD/MM')}</div>,
      key: 'TrainDate',
    },
    {
      title: t('table', 'CHART_CUSTOMERS'),
      dataIndex: 'customerSelection',
      key: 'Customers',
    },
    {
      title: t('table', 'CHART_STATIONS'),
      render: (record: PendingCommunicationsHistory) => {
        const concernedStations: string[] = [];
        record?.contents?.map((x) => {
          x.station && concernedStations.push(x.station.shortCode);
        });
        return concernedStations.map((concernedStation, index: number) => (
          <div style={acceptStyle(concernedStations.length, index)} key={index}>
            {concernedStation}
          </div>
        ));
      },
      key: 'Stations',
    },
    {
      title: t('table', 'CHART_PROGRAMMATION_ELEMENTS'),
      key: 'Programmation',
      children: [
        {
          title: t('table', 'CHART_DAYS_BEFORE_CIRCULATION'),
          dataIndex: 'daysBeforeCirculation',
          key: 'DaysBefore',
        },
        {
          title: t('table', 'CHART_SENT_TIME'),
          render: (record: PendingCommunicationsHistory) => (
            <div>{moment(record.communicationTime).format('HH:mm')}</div>
          ),
          key: 'SendTime',
        },
      ],
    },
    {
      title: t('table', 'CHART_LAST_SEND_DETAILS'),
      render: (record: PendingCommunicationsHistory) =>
        record?.contents?.map((currentRecord, index) => (
          <div key={index} style={acceptStyle(currentRecord.impactedStations.length, index)}>
            <SearchOutlined
              key={index}
              onClick={() => {
                setRecord(currentRecord);
                setModalVisible(true);
              }}
              style={{ display: 'block', color: '#00ACD0', fontSize: 20 }}
            />
          </div>
        )),
      key: 'Nature',
    },
    {
      title: (
        <>
          <div>{t('table', 'COLUMN_DELETE')}</div>
          <UnstyledButton className="deleteButton">
            <DeleteOutlined style={{ color: 'red', fontSize: '20px', marginLeft: '10px' }} onClick={onClickDelete} />
          </UnstyledButton>
        </>
      ),
      key: 'modifier',
      display: canWriteDefferedCommunications,
      shouldBeAdmin: true,
      render: (record: PendingCommunicationsHistory) => (
        <Checkbox key={record?.communicationID} onChange={(e) => handleSelectMessages(e, record)} />
      ),
    },
  ];

  const filteredColumns = columns
    .map((column) => {
      if (column.display !== false) return column;
    })
    .filter((column) => {
      return column !== undefined;
    });

  const historicPendingCommDataSource = data?.result?.map((item, index) => ({
    key: `${item.serviceName}-${index}`,
    ...item,
  }));

  return (
    <>
      {record && (
        <CommunicationHistoryModal onClose={() => setModalVisible(false)} isOpen={modalVisible} data={record} />
      )}
      {data && (
        <>
          <StyledTable
            pagination={false}
            dataSource={historicPendingCommDataSource}
            columns={filteredColumns as typeof columns}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('empty')} /> }}
          />
          <PaginationBlock
            data={{ pageNumber: data?.pageNumber, maxPages: data?.maxPages, maxResultPage: data?.maxResultPage }}
            onChange={(page: number, pageSize?: number) => {
              dispatch(
                historicPendingCommunicationActions.fetchHistoricPendingCommunication({
                  ...filterValues,
                  pageNumber: page,
                  maxResultPage: pageSize,
                })
              );
            }}
          />
        </>
      )}
    </>
  );
};

export default HistorisationPendingList;
